import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { api, downloadPdf, formatApiErrorDetail } from '@/lib/api';
import DayTabs from '@/components/DayTabs';
import OrderGrid from '@/components/OrderGrid';
import SummaryPanels from '@/components/SummaryPanels';
import PdfDropzone from '@/components/PdfDropzone';
import LahdePanel from '@/components/LahdePanel';
import { PresenceList } from '@/components/PresenceAvatar';
import { useRealtime } from '@/lib/realtime';
import { PRESENCE, LAHETE, SUMMARY } from '@/constants/testIds';
import { Filter, Search, X, Truck, Printer, AlertCircle, CheckSquare, Download, CalendarClock, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import ColumnFilter from '@/components/ColumnFilter';

export default function DayView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get('date');
  const [days, setDays] = useState([]);
  // Honour ?date=YYYY-MM-DD from URL (e.g. from Dashboard quick links).
  // Default: tomorrow's date (user is preparing the next delivery day).
  const [current, setCurrent] = useState(() => initialDate || format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [reference, setReference] = useState({ ryhma: [], ketju: [], reitti: [], status: [] });
  const [routes, setRoutes] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [onlyPending, setOnlyPending] = useState(false);
  const [data, setData] = useState({ date: '', rows: [] });
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(new Set());
  const [externalMeta, setExternalMeta] = useState(null);
  // Asiakas-sarakkeen järjestys. Oletuksena nouseva aakkosjärjestys (`'asiakas-asc'`)
  // jotta lista on aina luettavasti yhtenäinen. Käyttäjä voi vaihtaa laskevaan
  // klikkaamalla sarakeotsikkoa.
  const [sortKey, setSortKey] = useState('asiakas-asc');
  const toggleSort = useCallback(() => {
    setSortKey((cur) => (cur === 'asiakas-asc' ? 'asiakas-desc' : 'asiakas-asc'));
  }, []);

  // loadDay on määritettävä ENNEN handleOrderUpdate:a jotta hook-järjestys
  // ei aiheuta 'cannot access before initialization' -virhettä.
  const loadDay = useCallback(async (date) => {
    if (!date) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${date}`);
      setData(data);
    } catch {
      toast.error('Päivän lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOrderUpdate = useCallback((msg) => {
    let unknownCustomer = false;
    setData((d) => {
      if (d.date !== msg.date) return d;
      const exists = d.rows.find((r) => r.customer.id === msg.customer_id);
      if (!exists) {
        // Uusi asiakas päivälle (esim. IMAP-tuoma Prisma/S-Market ostotilaus)
        // — vain koko päivän uudelleenlataus voi lisätä puuttuvan rivin.
        unknownCustomer = true;
        return d;
      }
      return {
        ...d,
        rows: d.rows.map((r) => (r.customer.id === msg.customer_id ? { ...r, order: msg.order } : r)),
      };
    });
    if (unknownCustomer) {
      // Poimi tuoreet rivit backendistä koko päivälle → tase ylhäällä
      // ja uudet asiakasrivit ilmestyvät reaaliaikaisesti.
      loadDay(msg.date);
    }
    setTouched((s) => {
      const ns = new Set(s);
      ns.add(msg.customer_id);
      return ns;
    });
    setTimeout(() => {
      setTouched((s) => {
        const ns = new Set(s);
        ns.delete(msg.customer_id);
        return ns;
      });
    }, 1800);
  }, [loadDay]);

  const handleDailyMeta = useCallback((msg) => {
    setExternalMeta(msg);
  }, []);

  const { connected, presence, setEditing, clientId } = useRealtime({
    date: current,
    onOrderUpdate: handleOrderUpdate,
    onDailyMeta: handleDailyMeta,
  });

  const loadDays = useCallback(async () => {
    try {
      const { data } = await api.get('/days');
      setDays(data);
      if (!data.includes(current) && data.length > 0) {
        // Prefer tomorrow → today → next available day
        const today = format(new Date(), 'yyyy-MM-dd');
        const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
        const nextAvailable = data.find((d) => d >= today) || data[0];
        setCurrent(
          data.includes(tomorrow) ? tomorrow :
          data.includes(today) ? today :
          nextAvailable,
        );
      }
    } catch {
      // ignore: backend not reachable
    }
  }, [current]);

  const loadReference = useCallback(async () => {
    try {
      const { data } = await api.get('/reference');
      setReference(data);
    } catch {
      // ignore: backend not reachable
    }
  }, []);

  const loadRoutes = useCallback(async () => {
    try {
      const { data } = await api.get('/routes');
      setRoutes(data || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadDays();
    loadReference();
    loadRoutes();
  }, [loadDays, loadReference, loadRoutes]);

  useEffect(() => {
    loadDay(current);
  }, [current, loadDay]);

  const onPatch = useCallback(async (customerId, patch) => {
    setData((d) => ({
      ...d,
      rows: d.rows.map((r) =>
        r.customer.id === customerId
          ? { ...r, order: { ...(r.order || { id: '', date: d.date, customer_id: customerId, maara_kg: 0, status: '', lahete: '', ostotilaus: '' }), ...patch } }
          : r
      ),
    }));
    try {
      const { data: updated } = await api.patch(`/orders/${current}/${customerId}`, patch);
      setData((d) => ({
        ...d,
        rows: d.rows.map((r) => (r.customer.id === customerId ? { ...r, order: updated } : r)),
      }));
      setTouched((s) => new Set(s).add(customerId));
      setTimeout(() => {
        setTouched((s) => {
          const ns = new Set(s);
          ns.delete(customerId);
          return ns;
        });
      }, 1500);
    } catch {
      toast.error('Tallennus epäonnistui');
      loadDay(current);
    }
  }, [current, loadDay]);

  const onPdfsUploaded = useCallback(async (results) => {
    const ok = results.filter((r) => r.ok);
    const fail = results.filter((r) => !r.ok);
    if (ok.length) toast.success(`${ok.length} tilausta lisätty`);
    if (fail.length) toast.error(`${fail.length} epäonnistui`);
    await loadDays();
    const uploadedDates = Array.from(new Set(ok.map((r) => r.date)));
    if (uploadedDates.length > 0 && !uploadedDates.includes(current)) {
      setCurrent(uploadedDates[0]);
    } else {
      loadDay(current);
    }
  }, [current, loadDay, loadDays]);

  const rows = data.rows || [];
  const [onlyFilled, setOnlyFilled] = useState(false);
  const [query, setQuery] = useState('');

  // Per-column multi-select filters (Ryhmä, Reitti). Persisted to localStorage.
  const [gridFilters, setGridFilters] = useState(() => {
    try {
      const raw = localStorage.getItem('dayview.gridFilters');
      if (raw) {
        const v = JSON.parse(raw);
        return {
          ryhma: Array.isArray(v?.ryhma) ? v.ryhma : [],
          ketju: Array.isArray(v?.ketju) ? v.ketju : [],
          reitti: Array.isArray(v?.reitti) ? v.reitti : [],
        };
      }
    } catch {
      // ignore
    }
    return { ryhma: [], ketju: [], reitti: [] };
  });

  useEffect(() => {
    try {
      localStorage.setItem('dayview.gridFilters', JSON.stringify(gridFilters));
    } catch {
      // ignore
    }
  }, [gridFilters]);

  const updateGridFilter = (key) => (next) => {
    setGridFilters((g) => ({ ...g, [key]: next }));
  };

  const gridFilterCount = (gridFilters.ryhma?.length || 0) + (gridFilters.ketju?.length || 0) + (gridFilters.reitti?.length || 0);

  // Option lists derived from all customers in current day
  const gridFilterOptions = useMemo(() => {
    const ryhma = new Set();
    const ketju = new Set();
    const reitti = new Set();
    for (const r of rows) {
      if (r.customer.ryhma) ryhma.add(r.customer.ryhma);
      if (r.customer.ketju) ketju.add(r.customer.ketju);
      const rname = r.order?.reitti || r.customer.reitti;
      if (rname) reitti.add(rname);
    }
    return {
      ryhma: Array.from(ryhma),
      ketju: Array.from(ketju),
      reitti: Array.from(reitti),
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    let r = rows;
    if (gridFilters.ryhma.length > 0) {
      const s = new Set(gridFilters.ryhma);
      r = r.filter((row) => s.has(row.customer.ryhma));
    }
    if (gridFilters.ketju.length > 0) {
      const s = new Set(gridFilters.ketju);
      r = r.filter((row) => s.has(row.customer.ketju));
    }
    if (gridFilters.reitti.length > 0) {
      const s = new Set(gridFilters.reitti);
      r = r.filter((row) => s.has(row.order?.reitti || row.customer.reitti));
    }
    if (onlyFilled) r = r.filter((row) => (row.order?.maara_kg || 0) > 0);
    if (onlyPending) {
      r = r.filter((row) => {
        const o = row.order || {};
        if ((o.maara_kg || 0) <= 0) return false;
        const status = (o.status || '').trim();
        return status === '' || status === 'Arvio';
      });
    }
    const q = query.trim().toLowerCase();
    if (q) {
      r = r.filter((row) => {
        const c = row.customer;
        return (
          (c.asiakas || '').toLowerCase().includes(q) ||
          (c.ketju || '').toLowerCase().includes(q) ||
          (c.ryhma || '').toLowerCase().includes(q) ||
          (c.nro || '').toLowerCase().includes(q) ||
          (row.order?.ostotilaus || '').toLowerCase().includes(q)
        );
      });
    }
    // Sorting:
    //  • sortKey === 'asiakas-asc' / 'asiakas-desc' → explicit user choice
    //    (set by clicking the column header), wins over everything
    //  • muutoin: kun käyttäjä on suodattanut (haku, ryhmä-, ketju- tai
    //    reittisuodatin), järjestä asiakkaat aakkosittain (A→Ö). Ilman
    //    suodattimia oletusjärjestys (esim. reitti/nro) säilyy.
    const narrowed = Boolean(q)
      || gridFilters.ryhma.length > 0
      || gridFilters.ketju.length > 0
      || gridFilters.reitti.length > 0;
    if (sortKey === 'asiakas-asc' || sortKey === 'asiakas-desc' || narrowed) {
      const dir = sortKey === 'asiakas-desc' ? -1 : 1;
      r = [...r].sort((a, b) =>
        dir * (a.customer.asiakas || '').localeCompare(b.customer.asiakas || '', 'fi', { sensitivity: 'base' })
      );
    }
    return r;
  }, [rows, onlyFilled, onlyPending, query, gridFilters, sortKey]);

  const filledCount = rows.filter((row) => (row.order?.maara_kg || 0) > 0).length;
  // Count all customers slated for a carrier route (i.e. any route that
  // isn't 'Nouto' = customer self-pickup). Matches the transport-order
  // PDF inclusion logic so the counter accurately reflects how many rows
  // the carrier will get.
  const kpFilledCount = rows.filter((row) => {
    const route = (row.order?.reitti || row.customer.reitti || '').trim();
    return (row.order?.maara_kg || 0) > 0 && route && route !== 'Nouto';
  }).length;
  const pendingCount = useMemo(() => rows.filter((row) => {
    const o = row.order || {};
    if ((o.maara_kg || 0) <= 0) return false;
    const status = (o.status || '').trim();
    return status === '' || status === 'Arvio';
  }).length, [rows]);

  // Group counts using route.print_group when available
  const routesByName = useMemo(() => {
    const m = new Map();
    for (const r of routes) m.set(r.name, r);
    return m;
  }, [routes]);

  // Reverse map: print_group key → member route names. Used by the transport
  // preview popover to show e.g. "JM (kattaa: JM A, JM B)" so the user
  // understands which underlying routes a group rasti covers.
  const groupMembers = useMemo(() => {
    const m = new Map();
    for (const r of routes) {
      const pg = (r.print_group || '').trim();
      if (!pg) continue;
      if (!m.has(pg)) m.set(pg, []);
      m.get(pg).push(r.name);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.localeCompare(b, 'fi'));
    return m;
  }, [routes]);

  const routeCounts = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if ((r.order?.maara_kg || 0) <= 0) continue;
      // Do NOT default missing routes to "KP" — that wrongly inflated the
      // KP bucket whenever a customer/order had no route set at all. Keep
      // unrouted orders in a clearly-labelled "(Ei reittiä)" group so the
      // user can still spot them.
      const routeName = (r.order?.reitti || r.customer.reitti || '').trim() || '(Ei reittiä)';
      const rdoc = routesByName.get(routeName);
      const key = (rdoc?.print_group || '').trim() || routeName;
      m.set(key, (m.get(key) || 0) + 1);
    }
    return m;
  }, [rows, routesByName]);
  const routeOptions = useMemo(() => Array.from(routeCounts.keys()).sort((a, b) => a.localeCompare(b, 'fi')), [routeCounts]);
  const totalFilledOrders = useMemo(
    () => Array.from(routeCounts.values()).reduce((a, b) => a + b, 0),
    [routeCounts],
  );
  const ALL_ROUTES_KEY = '__ALL__';
  const [bulkRoute, setBulkRoute] = useState('');
  useEffect(() => {
    if (routeOptions.length === 0) { setBulkRoute(''); return; }
    if (bulkRoute === ALL_ROUTES_KEY) return; // keep "Kaikki" selection across reloads
    if (!routeOptions.includes(bulkRoute)) {
      setBulkRoute(routeOptions.includes('KP') ? 'KP' : routeOptions[0]);
    }
  }, [routeOptions, bulkRoute]);

  // Multi-select for the transport-order preview. The user picks which routes
  // get combined into one PDF (e.g. Auto 1 + Auto 3 + Nouto). On the first
  // render with new data we pre-tick every "Auto N" route so the default
  // "Esikatsele" click reproduces the carrier summary without extra clicks.
  const autoRouteOptions = useMemo(
    () => routeOptions.filter((r) => /^auto\s*\d+\s*$/i.test(r)),
    [routeOptions],
  );
  const [previewRoutes, setPreviewRoutes] = useState(() => new Set());
  const [previewRoutesInitialised, setPreviewRoutesInitialised] = useState(false);
  useEffect(() => {
    if (previewRoutesInitialised) return;
    if (routeOptions.length === 0) return;
    setPreviewRoutes(new Set(autoRouteOptions));
    setPreviewRoutesInitialised(true);
  }, [routeOptions, autoRouteOptions, previewRoutesInitialised]);

  const togglePreviewRoute = (name) => {
    setPreviewRoutes((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const selectAllPreviewRoutes = () => setPreviewRoutes(new Set(routeOptions));
  const selectAutoPreviewRoutes = () => setPreviewRoutes(new Set(autoRouteOptions));
  const clearPreviewRoutes = () => setPreviewRoutes(new Set());

  const previewRoutesCount = previewRoutes.size;
  const previewRoutesKgTotal = useMemo(() => {
    let kg = 0;
    for (const r of rows) {
      if ((r.order?.maara_kg || 0) <= 0) continue;
      const name = (r.order?.reitti || r.customer.reitti || '').trim() || '(Ei reittiä)';
      if (previewRoutes.has(name)) kg += r.order?.maara_kg || 0;
    }
    return kg;
  }, [rows, previewRoutes]);

  const downloadRouteLahetteet = async () => {
    if (!bulkRoute) return;
    try {
      const isAll = bulkRoute === ALL_ROUTES_KEY;
      const url = isAll
        ? `/api/lahete/all.pdf?date=${encodeURIComponent(current)}`
        : `/api/lahete/route.pdf?date=${encodeURIComponent(current)}&group_key=${encodeURIComponent(bulkRoute)}`;
      const filename = isAll
        ? `Lahetteet_kaikki_${current}.pdf`
        : `Lahetteet_${bulkRoute.replace(/[^A-Za-z0-9_-]+/g, '_')}_${current}.pdf`;
      const fname = await downloadPdf(url, filename);
      toast.success(`Lähetteet tulostettu (${fname})`);
      // Refetch koko päivä jotta 'Tehty'-status ilmestyy myös silloin kun
      // WebSocket-broadcast ei ehtinyt/kadonnut.
      await loadDay(current);
    } catch (e) {
      toast.error(`Lähetteet ei onnistunut: ${e.message}`);
    }
  };

  // Reset selection on day change
  useEffect(() => { setSelectedIds(new Set()); }, [current]);

  const toggleSelect = useCallback((customerId) => {
    setSelectedIds((s) => {
      const ns = new Set(s);
      if (ns.has(customerId)) ns.delete(customerId);
      else ns.add(customerId);
      return ns;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectAllVisibleFilled = useCallback(() => {
    setSelectedIds((s) => {
      const ns = new Set(s);
      for (const row of visibleRows) {
        if ((row.order?.maara_kg || 0) > 0) ns.add(row.customer.id);
      }
      return ns;
    });
  }, [visibleRows]);

  const downloadSelectedLahetteet = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds).join(',');
    try {
      const fname = await downloadPdf(
        `/api/lahete/selected.pdf?date=${current}&ids=${encodeURIComponent(ids)}`,
        `Lahetteet_valitut_${current}.pdf`,
      );
      toast.success(`${selectedIds.size} lähete${selectedIds.size === 1 ? '' : 'ttä'} tulostettu (${fname})`);
      clearSelection();
      // Refetch koko päivä jotta 'Tehty'-status ilmestyy heti kaikille.
      await loadDay(current);
    } catch (e) {
      toast.error(`Lähetteet ei onnistunut: ${e.message}`);
    }
  };

  const [transportLastSentAt, setTransportLastSentAt] = useState(null);
  const [transportAvailable, setTransportAvailable] = useState({ jm: [], individual: [] });

  useEffect(() => {
    // Lataa käytettävissä olevat kuljetusryhmät ja per-reitti-valinnat
    if (!current) return;
    api.get(`/transport-order/available/${current}`)
      .then(({ data }) => setTransportAvailable({ jm: data.jm || [], individual: data.individual || [] }))
      .catch(() => setTransportAvailable({ jm: [], individual: [] }));
  }, [current, data]);
  const [copyPrevWeekBusy, setCopyPrevWeekBusy] = useState(false);

  // Reset "viimeksi lähetetty" when day changes — it's per-day info
  useEffect(() => { setTransportLastSentAt(null); }, [current]);

  // Copy last week's orders → this day as estimates (status="Arvio").
  // Safe to re-run: existing entries are never overwritten.
  const copyFromPreviousWeek = async () => {
    if (copyPrevWeekBusy) return;
    setCopyPrevWeekBusy(true);
    try {
      const r = await api.post(`/orders/${current}/copy-from-previous-week`);
      const { copied = 0, skipped_existing = 0, source_date, message } = r.data || {};
      if (message) {
        toast.info(message);
      } else if (copied === 0 && skipped_existing > 0) {
        toast.info(`Ei uusia arvioita — kaikilla ${skipped_existing} asiakkaalla on jo tilaus tälle päivälle.`);
      } else {
        toast.success(`Kopioitiin ${copied} arviota viikolta ${source_date}${skipped_existing ? ` (${skipped_existing} ohitettu, oli jo tilaus)` : ''}`);
      }
      // Reload current day so the new estimates appear
      await loadDay(current);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Arvioiden kopiointi epäonnistui');
    } finally {
      setCopyPrevWeekBusy(false);
    }
  };

  const downloadTransportEml = async (opts = {}) => {
    const { group = null, routes = null } = opts;
    try {
      const token = localStorage.getItem('access_token');
      // 1) Download PDF
      const qs = new URLSearchParams();
      if (group) qs.set('group', group);
      if (routes && routes.length > 0) routes.forEach((r) => qs.append('routes', r));
      const query = qs.toString();
      const pdfResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transport-order/${current}.pdf${query ? '?' + query : ''}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!pdfResp.ok) throw new Error(`HTTP ${pdfResp.status}`);
      const blob = await pdfResp.blob();
      const dispo = pdfResp.headers.get('content-disposition') || '';
      const fname = dispo.match(/filename="([^"]+)"/)?.[1] || `Kuljetustilaus_${current}.pdf`;
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      // 2) Open Outlook with mailto: prefilled + Bcc:hen kopio Vesalle
      const [y, m, d] = current.split('-');
      const dateFi = `${parseInt(d, 10)}.${parseInt(m, 10)}.${y}`;
      const to = 'marko.kalander@jarvimaki.com,ajojarjestely@jarvimaki.com';
      const bcc = 'vesa@tammilehto.eu';
      let label = 'JM';
      if (group === 'tampere_lahti') label = 'Tampere & Lahti';
      else if (routes && routes.length > 0) label = routes.join(' & ');
      const subjectStr = `Kuljetustilaus – ${label} ${dateFi}`;
      const bodyStr = `Hei,\n\nliitteenä kuljetustilaus – ${label} ${dateFi}.\n`;
      const subject = encodeURIComponent(subjectStr);
      const body = encodeURIComponent(bodyStr);
      setTimeout(() => {
        window.location.href = `mailto:${to}?bcc=${bcc}&subject=${subject}&body=${body}`;
      }, 300);

      setTransportLastSentAt(new Date());
      toast.success(`PDF ladattu (${label}: ${fname}). Kopio Bcc:llä vesa@tammilehto.eu.`, {
        duration: 8000,
      });
    } catch {
      toast.error('Kuljetustilauksen luonti epäonnistui');
    }
  };

  const previewTransportPdf = () => {
    if (previewRoutes.size === 0) {
      toast.error('Valitse vähintään yksi reitti esikatselua varten');
      return;
    }
    const token = localStorage.getItem('access_token');
    // Send selected routes as repeated `routes=` query params so the backend
    // can combine arbitrary chosen routes into one PDF.
    const routesParam = Array.from(previewRoutes)
      .map((r) => `routes=${encodeURIComponent(r)}`)
      .join('&');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transport-order/${current}.pdf?_=${Date.now()}&${routesParam}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then((r) => r.blob())
      .then((blob) => window.open(URL.createObjectURL(blob), '_blank'))
      .catch(() => toast.error('PDF-esikatselu epäonnistui'));
  };

  return (
    <div className="space-y-6">
      <DayTabs days={days} current={current} onChange={setCurrent} />

      <div className="flex items-center justify-between flex-wrap gap-2" data-testid={PRESENCE.list}>
        <PresenceList users={presence} currentClientId={clientId} />
        <div className="flex items-center gap-1.5 text-xs">
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-[#3B6E44]' : 'bg-[#C05A3C]'}`} />
          <span className="text-[#6A6D66]">{connected ? 'Reaaliaikainen synkka päällä' : 'Yhdistetään…'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SummaryPanels rows={rows} />
        <LahdePanel date={current} externalMeta={externalMeta} />
      </div>

      <div className="space-y-2">
        <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 bg-[#F9F9F6]/95 backdrop-blur-md border-b border-[#E5E6E3]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div
                data-testid="dayview-sticky-day"
                title="Tarkasta että kirjaat oikealle päivälle"
                className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#2A4B30] text-white shrink-0 shadow-sm"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider opacity-80">
                  {(() => {
                    const w = ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'];
                    return current ? w[new Date(current + 'T00:00').getDay()] : '';
                  })()}
                </span>
                <span className="text-sm font-mono tabular-nums">
                  {current ? (() => { const [y,m,d] = current.split('-'); return `${parseInt(d,10)}.${parseInt(m,10)}.${y}`; })() : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOnlyFilled((v) => !v)}
                data-testid="filter-only-filled-toggle"
                className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  onlyFilled
                    ? 'bg-[#2A4B30] text-white hover:bg-[#1E3622]'
                    : 'bg-white border border-[#E5E6E3] text-[#1C1C1A] hover:bg-[#F4F6F3]'
                }`}
              >
                <Filter size={13} />
                Vain täytetyt
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${onlyFilled ? 'bg-white/20' : 'bg-[#E5E6E3] text-[#6A6D66]'}`}>
                  {filledCount}
                </span>
              </button>
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={() => setOnlyPending((v) => !v)}
                data-testid={SUMMARY.pendingBadge}
                className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  onlyPending
                    ? 'bg-[#C28D2F] text-white hover:bg-[#9F751F]'
                    : 'bg-[#FDF6EB] border border-[#F0E0BD] text-[#8A6A1F] hover:bg-[#F7EAC9]'
                }`}
                title="Näytä vain keskeneräiset (Arvio tai status puuttuu)"
              >
                <AlertCircle size={13} />
                Keskeneräisiä
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${onlyPending ? 'bg-white/20' : 'bg-white text-[#8A6A1F] border border-[#F0E0BD]'}`}>
                  {pendingCount}
                </span>
              </button>
            )}
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6A6D66]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Hae kauppa, nro, ostotilaus…"
                data-testid="filter-search-input"
                className="h-8 pl-7 pr-7 w-64 text-xs"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-[#E5E6E3] flex items-center justify-center text-[#6A6D66]"
                  aria-label="Tyhjennä haku"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 border-l border-[#E5E6E3] pl-2 ml-1">
              <ColumnFilter
                label="Ryhmä"
                options={gridFilterOptions.ryhma}
                selected={gridFilters.ryhma}
                onChange={updateGridFilter('ryhma')}
                testId="dayview-filter-ryhma"
              />
              <ColumnFilter
                label="Ketju"
                options={gridFilterOptions.ketju}
                selected={gridFilters.ketju}
                onChange={updateGridFilter('ketju')}
                testId="dayview-filter-ketju"
              />
              <ColumnFilter
                label="Reitti"
                options={gridFilterOptions.reitti}
                selected={gridFilters.reitti}
                onChange={updateGridFilter('reitti')}
                testId="dayview-filter-reitti"
              />
              {gridFilterCount > 0 && (
                <button
                  type="button"
                  onClick={() => setGridFilters({ ryhma: [], ketju: [], reitti: [] })}
                  data-testid="dayview-clear-grid-filters"
                  className="h-7 px-2 rounded-md text-[11px] font-medium bg-[#FBEAE5] text-[#C05A3C] hover:bg-[#F5D6CD] inline-flex items-center gap-1"
                  title="Tyhjennä suodattimet"
                >
                  <X size={11} /> Tyhjennä
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-1 bg-[#E8F0E9] border border-[#CFE0D2] rounded-full pl-3 pr-1 h-8">
                <CheckSquare size={13} className="text-[#2A4B30]" />
                <span className="text-xs font-medium text-[#1E3622]">
                  {selectedIds.size} valittu
                </span>
                <button
                  type="button"
                  onClick={downloadSelectedLahetteet}
                  data-testid={LAHETE.printSelectedButton}
                  className="h-6 px-2.5 rounded-full text-[11px] font-medium bg-[#2A4B30] hover:bg-[#1E3622] text-white flex items-center gap-1 transition-colors"
                  title="Tulosta valittujen asiakkaiden lähetteet yhteen PDF:ään"
                >
                  <Printer size={11} />
                  Tulosta valitut
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  data-testid={LAHETE.clearSelectionButton}
                  className="w-6 h-6 rounded-full hover:bg-[#CFE0D2] text-[#2A4B30] flex items-center justify-center"
                  title="Tyhjennä valinta"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            {routeOptions.length > 0 && (
              <div className="flex items-center gap-1">
                <Select value={bulkRoute} onValueChange={setBulkRoute}>
                  <SelectTrigger
                    data-testid={LAHETE.routeSelect}
                    className="h-8 w-32 text-xs border-[#E5E6E3]"
                  >
                    <SelectValue placeholder="Reitti" />
                  </SelectTrigger>
                  <SelectContent>
                    {routeOptions.length > 1 && (
                      <SelectItem value={ALL_ROUTES_KEY}>
                        Kaikki ({totalFilledOrders})
                      </SelectItem>
                    )}
                    {routeOptions.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r} ({routeCounts.get(r)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={downloadRouteLahetteet}
                  data-testid={LAHETE.routePrintButton}
                  disabled={!bulkRoute}
                  className="h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 bg-white border border-[#E5E6E3] text-[#1C1C1A] hover:bg-[#F4F6F3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Lataa kaikki reitin lähetteet yhtenä PDF:nä"
                >
                  <Printer size={13} />
                  Tulosta reitin lähetteet
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={copyFromPreviousWeek}
              disabled={copyPrevWeekBusy}
              data-testid="copy-prev-week-button"
              className="h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 bg-[#E9D9B5] border border-[#D9C595] text-[#2A4B30] hover:bg-[#E0CD9F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Kopioi viime viikon saman päivän tilaukset arvioiksi (status=Arvio). Ei korvaa olemassa olevia tilauksia."
            >
              <CalendarClock size={13} className={copyPrevWeekBusy ? 'animate-spin' : ''} />
              {copyPrevWeekBusy ? 'Kopioidaan…' : 'Esitäytä arvioista'}
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="transport-preview-routes-trigger"
                  className="h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 bg-white border border-[#E5E6E3] text-[#1C1C1A] hover:bg-[#F4F6F3] transition-colors"
                  title="Valitse mitkä reitit yhdistetään samaan kuljetustilaus-PDF:ään"
                >
                  Esikatsele PDF
                  <span className="font-mono text-[10px] bg-[#E9D9B5] text-[#2A4B30] px-1.5 py-0.5 rounded-full">
                    {previewRoutesCount}
                  </span>
                  <ChevronDown size={12} className="opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-72 p-0 bg-white border-[#E5E6E3]"
                data-testid="transport-preview-routes-popover"
              >
                <div className="px-3 py-2 border-b border-[#E5E6E3]">
                  <div className="text-[11px] uppercase tracking-wider text-[#6A6D66] font-semibold">
                    Yhdistä reitit PDF:ään
                  </div>
                  <div className="text-[10px] text-[#6A6D66] mt-0.5">
                    {previewRoutesCount} reittiä valittu · {previewRoutesKgTotal} kg
                  </div>
                </div>
                <div className="flex items-center gap-1 px-3 py-2 border-b border-[#E5E6E3] text-[10px]">
                  <button
                    type="button"
                    onClick={selectAutoPreviewRoutes}
                    data-testid="transport-preview-routes-select-auto"
                    className="px-2 py-0.5 rounded-full bg-[#E9D9B5] text-[#2A4B30] hover:bg-[#E0CD9F]"
                  >
                    Vain Auto-reitit
                  </button>
                  <button
                    type="button"
                    onClick={selectAllPreviewRoutes}
                    data-testid="transport-preview-routes-select-all"
                    className="px-2 py-0.5 rounded-full bg-white border border-[#E5E6E3] text-[#1C1C1A] hover:bg-[#F4F6F3]"
                  >
                    Kaikki
                  </button>
                  <button
                    type="button"
                    onClick={clearPreviewRoutes}
                    data-testid="transport-preview-routes-clear"
                    className="px-2 py-0.5 rounded-full bg-white border border-[#E5E6E3] text-[#6A6D66] hover:bg-[#F4F6F3]"
                  >
                    Tyhjennä
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {routeOptions.length === 0 && (
                    <div className="px-3 py-2 text-xs text-[#6A6D66]">
                      Ei reittejä tälle päivälle
                    </div>
                  )}
                  {routeOptions.map((r) => {
                    const checked = previewRoutes.has(r);
                    const members = groupMembers.get(r) || [];
                    const isGroup = members.length > 1 || (members.length === 1 && members[0] !== r);
                    return (
                      <label
                        key={r}
                        data-testid={`transport-preview-routes-item-${r}`}
                        className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#F4F6F3] ${
                          checked ? 'bg-[#F4F6F3]' : ''
                        }`}
                        title={isGroup ? `${r} kattaa reitit: ${members.join(', ')}` : undefined}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => togglePreviewRoute(r)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[#1C1C1A] truncate">{r}</span>
                          {isGroup && (
                            <span className="block text-[10px] text-[#6A6D66] truncate">
                              kattaa: {members.join(', ')}
                            </span>
                          )}
                        </span>
                        <span className="font-mono text-[10px] text-[#6A6D66]">
                          {routeCounts.get(r)}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="px-3 py-2 border-t border-[#E5E6E3]">
                  <button
                    type="button"
                    onClick={previewTransportPdf}
                    data-testid="transport-preview-button"
                    disabled={previewRoutesCount === 0}
                    className="w-full h-8 rounded-full text-xs font-medium bg-[#2A4B30] text-white hover:bg-[#1E3622] disabled:bg-[#E5E6E3] disabled:text-[#6A6D66] disabled:cursor-not-allowed transition-colors"
                  >
                    Avaa PDF ({previewRoutesCount} {previewRoutesCount === 1 ? 'reitti' : 'reittiä'})
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="transport-send-button"
                  disabled={kpFilledCount === 0}
                  className="h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 bg-[#2A4B30] text-white hover:bg-[#1E3622] disabled:bg-[#E5E6E3] disabled:text-[#6A6D66] disabled:cursor-not-allowed transition-colors"
                  title="Valitse mihin kuljetusryhmään lähetetään"
                >
                  <Truck size={13} />
                  {transportLastSentAt ? 'Lähetä uudelleen' : 'Lähetä kuljetustilaus'}
                  <span className="font-mono text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">
                    {kpFilledCount}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[240px]">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#6A6D66]">
                  Valitse kuljetusryhmä
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {transportAvailable.jm.length > 0 && (
                  <DropdownMenuItem
                    onClick={() => downloadTransportEml({})}
                    data-testid="transport-send-jm"
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">JM (Järvimäki)</span>
                      <span className="text-[10px] text-[#6A6D66]">
                        {transportAvailable.jm.join(', ')}
                      </span>
                    </div>
                  </DropdownMenuItem>
                )}
                {transportAvailable.individual.map((rname) => (
                  <DropdownMenuItem
                    key={rname}
                    onClick={() => downloadTransportEml({ routes: [rname] })}
                    data-testid={`transport-send-route-${rname}`}
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{rname}</span>
                      <span className="text-[10px] text-[#6A6D66]">Vain tämä reitti</span>
                    </div>
                  </DropdownMenuItem>
                ))}
                {transportAvailable.individual.length > 1 && (
                  <DropdownMenuItem
                    onClick={() => downloadTransportEml({ routes: transportAvailable.individual })}
                    data-testid="transport-send-individual-all"
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{transportAvailable.individual.join(' & ')}</span>
                      <span className="text-[10px] text-[#6A6D66]">Kaikki yllä olevat yhdessä</span>
                    </div>
                  </DropdownMenuItem>
                )}
                {transportAvailable.jm.length === 0 && transportAvailable.individual.length === 0 && (
                  <div className="px-3 py-2 text-xs text-[#6A6D66]">Ei reittejä joilla tilauksia tänään</div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {transportLastSentAt && (
              <span
                data-testid="transport-last-sent"
                className="text-[11px] text-[#6A6D66] flex items-center gap-1"
                title="Voit lähettää uudelleen jos tilauksissa on muutoksia"
              >
                ✓ klo {transportLastSentAt.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              onClick={async () => {
                try {
                  const fname = await downloadPdf(`/api/orders/${current}/export.csv`, `tilaukset_${current}.csv`);
                  toast.success(`Ladattu: ${fname}`);
                } catch (e) {
                  toast.error(`CSV-vienti epäonnistui: ${e.message || e}`);
                }
              }}
              data-testid="dayview-export-csv-button"
              className="h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 border border-[#E5E6E3] bg-white hover:bg-[#F4F6F3] text-[#1C1C1A] transition-colors"
              title="Vie päivän tilaukset CSV-tiedostoksi (Excel-yhteensopiva)"
            >
              <Download size={13} />
              Vie CSV
            </button>
            <div className="text-xs text-[#6A6D66] ml-1">
              {visibleRows.length} / {rows.length} riviä
            </div>
          </div>
        </div>
        </div>
        {loading && (
          <div className="text-xs text-[#6A6D66]">Ladataan…</div>
        )}
        <OrderGrid
          rows={visibleRows}
          reference={reference}
          onPatch={onPatch}
          recentlyTouched={touched}
          presence={presence}
          currentClientId={clientId}
          setEditing={setEditing}
          currentDate={current}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSelectAllVisible={selectAllVisibleFilled}
          onClearSelection={clearSelection}
          sortKey={sortKey}
          onToggleSort={toggleSort}
          onLaheteDone={() => loadDay(current)}
        />
      </div>

      <PdfDropzone onUploaded={onPdfsUploaded} />
    </div>
  );
}
